import '.e{display:grid;}.ssr-plugin'
import '.p{gap:1.5rem;}.ssr-plugin'
import '.gs{grid-template-columns:repeat(4, minmax(250px, 1fr));}.ssr-plugin'
import { useTranslations } from '@i18n/useLanguage'
import { SectionHeader } from '@app/components/blocks/sectionHeader/SectionHeader'
import { pages } from '@app/util/links'
import { HousingFormCard } from '@app/components/blocks/card/HousingFormCard'
import { Style, styled } from 'inlines'
import { useQuery } from '@based/sdk/react'
import { useMemo } from 'react'
import { HousingFormSpotlightModel } from 'functions/app/spotlight/list'
import { HousingFormListModel } from 'functions/app/housing-form/list'
import defaultHousingFormPicture from '@app/assets/defaultHousingFormPicture.png'
import { useLocation } from 'wouter'
import useMediaQuery from '@app/util/useMediaQuery'
import Column from '@app/components/layout/Column'

const COUNT = 3

const HousingFormCardItem = ({
  housingForm,
  onClick,
  style,
}: {
  housingForm: Omit<HousingFormListModel, 'initiatives'>
  onClick: () => void
  style?: Style
}) => {
  const followerCountQuery = useQuery('count-followers', {
    entityType: 'housingForm',
    entityIds: [housingForm.id],
  })

  return (
    <HousingFormCard
      image={housingForm.images?.[0]?.src ?? defaultHousingFormPicture}
      title={housingForm.title}
      followers={housingForm.followers ?? []}
      followerCount={followerCountQuery.data?.[0] ?? 0}
      onClick={onClick}
      style={style}
    />
  )
}

export const HousingFormsSection = () => {
  const isMobile = useMediaQuery()
  const [, navigate] = useLocation()
  const t = useTranslations().app
  const spotlightQuery = useQuery('list-spotlight', {
    entityType: 'housingForm',
    limit: COUNT,
  })
  const fallbackQuery = useQuery('list-housing-forms', {
    top: COUNT,
  })

  const housingForms: Omit<HousingFormListModel, 'initiatives'>[] =
    useMemo(() => {
      const spotlights: HousingFormSpotlightModel[] = spotlightQuery.data ?? []
      const fallback: HousingFormListModel[] = fallbackQuery.data ?? []
      const spotlighted = spotlights.map((s) => s.housingForm)

      if (spotlighted.length === COUNT) {
        return spotlighted
      }

      const nonDuplicated = fallback.filter(
        (fi) => spotlighted.findIndex((i) => i.id === fi.id) === -1,
      )

      return [...spotlighted, ...nonDuplicated].slice(0, COUNT)
    }, [spotlightQuery.data, fallbackQuery.data])

  return (
    <Column alignItems="stretch">
      <SectionHeader
        title={t.landingPage.housingFormsSection.title}
        subtitle={t.landingPage.housingFormsSection.subTitle}
        seeMore={t.landingPage.housingFormsSection.seeMore}
        toRoute={pages.housingForms}
      />

      <styled.div className="e p gs"
        style={{
          '@media (max-width: 1200px)': {
            gridTemplateColumns: 'repeat(2, minmax(250px, 1fr))',
          },
          '@media (max-width: 800px)': {
            gridTemplateColumns: 'repeat(1, minmax(250px, 1fr))',
          },
        }}
      >
        {housingForms.map((housingForm, index) => (
          <HousingFormCardItem
            key={housingForm.id}
            housingForm={housingForm}
            onClick={() => navigate(pages.housingForm(housingForm.slug))}
            style={{
              gridColumn: index === 0 && !isMobile ? '1 / span 2' : null,
            }}
          />
        ))}
      </styled.div>
    </Column>
  )
}
